import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import './Affiliates.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const emptyForm = { id: null, code: '', description: '', is_active: true };

function Affiliates() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [landingBaseUrl, setLandingBaseUrl] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAffiliates();
    fetchLandingBaseUrl();
  }, []);

  // Close modal on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (formModalOpen) closeFormModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [formModalOpen]);

  const fetchLandingBaseUrl = async () => {
    // Priority: setting from DB > REACT_APP_LANDING_URL env > auto-detect from admin host
    let base = '';
    try {
      const res = await fetch(`${API_URL}/settings`);
      if (res.ok) {
        const data = await res.json();
        if (data && typeof data.landing_page_url === 'string') {
          base = data.landing_page_url.trim();
        }
      }
    } catch (e) {
      // ignore, fall back below
    }

    if (!base && process.env.REACT_APP_LANDING_URL) {
      base = process.env.REACT_APP_LANDING_URL;
    }

    if (!base && typeof window !== 'undefined') {
      const host = window.location.host.replace(/^admin\./, 'www.');
      base = `${window.location.protocol}//${host}`;
    }

    setLandingBaseUrl(base.replace(/\/$/, ''));
  };

  const fetchAffiliates = async () => {
    const token = localStorage.getItem('admin_token');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/affiliates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAffiliates(data);
      } else {
        toast.error('Failed to load affiliates');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setIsEditing(false);
    setFormModalOpen(true);
  };

  const openEditModal = (affiliate) => {
    setForm({
      id: affiliate.id,
      code: affiliate.code,
      description: affiliate.description || '',
      is_active: affiliate.is_active === 1 || affiliate.is_active === true
    });
    setIsEditing(true);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormModalOpen(false);
    setForm(emptyForm);
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    const trimmedCode = form.code.trim();
    if (!trimmedCode) {
      toast.error('Code is required');
      return;
    }
    if (!/^[A-Za-z0-9_-]+$/.test(trimmedCode)) {
      toast.error('Code can only contain letters, numbers, underscores and dashes');
      return;
    }

    setSaving(true);
    try {
      const url = isEditing
        ? `${API_URL}/affiliates/${form.id}`
        : `${API_URL}/affiliates`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          code: trimmedCode,
          description: form.description.trim(),
          is_active: form.is_active
        })
      });

      if (response.ok) {
        toast.success(isEditing ? 'Affiliate updated' : 'Affiliate created');
        setFormModalOpen(false);
        setForm(emptyForm);
        setIsEditing(false);
        fetchAffiliates();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || 'Save failed');
      }
    } catch (error) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const buildAffiliateUrl = (code) => {
    if (!landingBaseUrl) return `?code=${encodeURIComponent(code)}`;
    return `${landingBaseUrl}/?code=${encodeURIComponent(code)}`;
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Link copied');
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const filteredAffiliates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return affiliates;
    return affiliates.filter(a =>
      (a.code || '').toLowerCase().includes(q) ||
      (a.description || '').toLowerCase().includes(q)
    );
  }, [affiliates, search]);

  const hasConfiguredDomain = !!(landingBaseUrl && !landingBaseUrl.includes('admin.'));

  return (
    <div className="affiliates-page">
      {!hasConfiguredDomain && (
        <div className="info-banner">
          <span className="info-icon">💡</span>
          <div>
            <strong>Landing Page URL not configured.</strong> The URLs below are auto-generated from the current host.
            Set it in <a href="/settings">Page Settings → Landing Page URL</a> for correct affiliate links.
          </div>
        </div>
      )}

      {/* Toolbar: search + add */}
      <div className="affiliates-toolbar">
        <div className="toolbar-search">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by code or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="clear-search-btn"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        <div className="toolbar-actions">
          <span className="total-count">
            {search ? `${filteredAffiliates.length} of ${affiliates.length}` : `Total: ${affiliates.length}`}
          </span>
          <button
            type="button"
            className="btn btn-primary"
            onClick={openAddModal}
          >
            ➕ Add Affiliate
          </button>
        </div>
      </div>

      {/* List */}
      <section className="affiliate-list-card">
        {loading ? (
          <div className="loading-container">
            <div className="loader"></div>
          </div>
        ) : affiliates.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <h3>No affiliates yet</h3>
            <p>Click <strong>Add Affiliate</strong> to create your first one.</p>
          </div>
        ) : filteredAffiliates.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🔍</span>
            <h3>No matches</h3>
            <p>No affiliates match "{search}".</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>URL</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAffiliates.map(a => {
                  const url = buildAffiliateUrl(a.code);
                  const isAbsolute = /^https?:\/\//i.test(url);
                  return (
                    <tr key={a.id}>
                      <td className="code-cell">{a.code}</td>
                      <td>{a.description || <span className="muted">—</span>}</td>
                      <td>
                        <span className={`status-badge ${a.is_active ? 'active' : 'inactive'}`}>
                          {a.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="link-cell">
                        <div className="link-inner">
                          {isAbsolute ? (
                            <a
                              className="link-text"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={url}
                            >
                              {url}
                            </a>
                          ) : (
                            <span className="link-text" title={url}>{url}</span>
                          )}
                          <button
                            type="button"
                            className="btn btn-secondary btn-xs"
                            onClick={() => copyToClipboard(url)}
                          >
                            📋 Copy
                          </button>
                        </div>
                      </td>
                      <td className="muted">{a.created_at || '-'}</td>
                      <td className="actions-cell">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditModal(a)}
                        >
                          ✏️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Add / Edit Modal */}
      {formModalOpen && (
        <div className="affiliate-modal-overlay" onClick={closeFormModal}>
          <div className="affiliate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="affiliate-modal-header">
              <h3>{isEditing ? '✏️ Edit Affiliate' : '➕ Add New Affiliate'}</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeFormModal}
                aria-label="Close"
                disabled={saving}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="affiliate-form">
              <div className="form-group">
                <label>Code <span className="required">*</span></label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="e.g. ABCD"
                  maxLength={50}
                  autoFocus
                  required
                />
                <small className="hint">
                  Letters, numbers, dashes and underscores only. Used in <code>?code=</code> URL parameter.
                </small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional label (e.g. partner name)"
                  maxLength={255}
                />
              </div>

              <div className="form-group checkbox-inline">
                <input
                  type="checkbox"
                  id="affiliate_is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <label htmlFor="affiliate_is_active">
                  Active (inactive codes are ignored on the landing page)
                </label>
              </div>

              <div className="affiliate-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (isEditing ? '💾 Update' : '➕ Add Affiliate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Affiliates;
